import React, { useState, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import styled from "styled-components";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import DeactivateIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  useDeactivateUsersDoctor,
  useDeleteUsersDoctor,
  //useGetAllPatients,
  useGetOrgUsers,
  useUserLoginData,
} from "../../api/ekoApi";
import { SearchInput } from "../../components/SearchInput";
import { UserTabs } from "../../components/AdminTabs";
import { useNavigate } from "react-router-dom";

const TabAndSearchWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  padding-right: 10px;
  align-items: center;
`;

const UsersInOrganization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { data: loggedUser } = useUserLoginData();
  const { data, isLoading, isError, refetch } = useGetOrgUsers(searchTerm);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const { mutate } = useDeleteUsersDoctor();
  const isActive = useDeactivateUsersDoctor();
  // const bla = useGetAllPatients()
  // console.log(bla.data)

  useEffect(() => {
    if (currentTab === 1) {
      navigate(`/${loggedUser?.organization}/patients`);
    }
    if (currentTab === 0) {
      navigate(`/${loggedUser?.organization}/users`);
    }
  }, [currentTab, navigate, loggedUser?.organization]);

  function handleDelete(rowData) {
    setDeleteModalOpen(true);
    setSelectedRowData(rowData);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value, refetch) => {
      refetch({ searchTerm: value });
    }, 400),
    []
  );

  function handleActivate(rowData) {
    setActivateModalOpen(true);
    setSelectedRowData(rowData);
  }

  function handleTabChange(event, newValue) {
    setCurrentTab(newValue);
  }

  const handleDeactivate = (rowData) => {
    setDeactivateModalOpen(true);
    setSelectedRowData(rowData);
    //isActive.mutate({id: rowData._id})
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRowData(null);
  };

  const handleCloseDeactivateModal = () => {
    setDeactivateModalOpen(false);
    setSelectedRowData(null);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setSelectedRowData(null);
  };

  const handleConfirmDelete = () => {
    mutate(selectedRowData._id);
    handleCloseDeleteModal();
  };

  const handleConfirmActivate = () => {
    isActive.mutate({ id: selectedRowData._id, isActive: true });
    handleCloseActivateModal();
  };

  const handleConfirmDeactivate = () => {
    isActive.mutate({ id: selectedRowData._id, isActive: false });
    handleCloseDeactivateModal();
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    handleSearch(value, refetch);
  };

  const columnDefsAdmins = [
    //{ field: "roles.User", headerName: t('role'), flex: 1 },
    { field: "displayName", headerName: t("displayName"), flex: 1, sortable: true  },
    { field: "email", headerName: t("emailLabel"), flex: 1, sortable: true  },
    { field: "organization", headerName: t("organization"), flex: 1, sortable: true  },
    { field: "subOrganization", headerName: t("suborganization"), sortable: true  },
    { field: "town", headerName: t("town"), sortable: true  },
    { field: "isActive", headerName: t("active"), sortable: true  },
    { field: "_id", hide: true },
    {
      headerName: t("actions"),
      cellRenderer: (params) => (
        <>
          <DeleteIcon
            onClick={() => handleDelete(params.data)}
            style={{ cursor: "pointer" }}
          />
          {params.data.isActive && (
            <DeactivateIcon
              onClick={() => handleDeactivate(params.data)}
              style={{
                cursor: "pointer",
                color: "#ff7b7b",
                marginLeft: "10px",
              }}
            />
          )}
          {!params.data.isActive && (
            <CheckCircleOutlineIcon
              onClick={() => handleActivate(params.data)}
              style={{ cursor: "pointer", color: "green", marginLeft: "10px" }}
            />
          )}
        </>
      ),
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading user data: {isError.message}</div>;
  }

  const rowData = data.users || [];

  return (
    <div>
      <TabAndSearchWrapper>
        <UserTabs currentTab={currentTab} handleTabChange={handleTabChange} />
        <SearchInput
          searchText={searchTerm}
          handleSearchInputChange={handleSearchInput}
        />
      </TabAndSearchWrapper>
      <div
        className="ag-theme-alpine"
        style={{ height: "80vh", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefsAdmins}
          localeText={{ noRowsToShow: t("noRowsToShow") }}
        ></AgGridReact>
      </div>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t("deleteConfirmation")}</DialogTitle>
        <DialogContent>
          {t("areYouSureToDelete")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDelete} color="error">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Modal */}
      <Dialog open={deactivateModalOpen} onClose={handleCloseDeactivateModal}>
        <DialogTitle>{t("deactivateConfirmation")}</DialogTitle>
        <DialogContent>
          {t("areYouSureDeactivate")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeactivateModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDeactivate}>{t("deactivate")}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={activateModalOpen} onClose={handleCloseDeactivateModal}>
        <DialogTitle>{t("activateUser")}</DialogTitle>
        <DialogContent>
          {t("areYouSureActivate")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmActivate}>{t("activate")}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UsersInOrganization;
